import { AudioLines, Pause, Play, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { WAVE_BAR_CSS, WAVE_GAP_CSS, WAVE_RADIUS_CSS, shapeWaveAmp } from '../lib/waveform-style';

interface Props {
  /** Object URL do blob gravado. */
  src: string;
  /** Duração estimada (em segundos) — vinda do timer da gravação. */
  estimatedDuration: number;
  onDiscard: () => void;
}

/**
 * Preview do áudio recém-gravado, antes do envio.
 *
 * UI: pílula larga substituindo o composer com:
 * - botão lixeira (descarta)
 * - botão play/pause
 * - waveform via WaveSurfer
 * - timer mm:ss / mm:ss
 * - badge "Original" (indicação de áudio sem alteração)
 */
export function AudioPreview({ src, estimatedDuration, onDiscard }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(estimatedDuration);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#94A3B8',
      progressColor: '#0F172A',
      cursorColor: '#0F172A',
      cursorWidth: 0,
      height: 28,
      // Desenha as barras com o MESMO estilo da gravação (waveform-style.ts):
      // normaliza pelo maior pico + gamma que realça as barras baixas. O
      // WaveSurfer clona este canvas e recolore pro progresso (play/seek ok).
      renderFunction: (peaks, ctx) => {
        const data = peaks[0];
        if (!data || data.length === 0) return;
        const { width, height } = ctx.canvas; // device px
        const dpr = window.devicePixelRatio || 1;
        const barW = Math.max(1, Math.round(WAVE_BAR_CSS * dpr));
        const gap = Math.max(1, Math.round(WAVE_GAP_CSS * dpr));
        const radius = Math.round(WAVE_RADIUS_CSS * dpr);
        const step = barW + gap;
        const minH = Math.max(1, Math.round(WAVE_BAR_CSS * dpr));
        const cy = height / 2;
        const bars = Math.max(1, Math.floor(width / step));
        const bin = data.length / bars;
        // Pico por barra + maior pico (normaliza igual à gravação ao vivo).
        const barPeaks = new Array<number>(bars);
        let maxPeak = 0;
        for (let b = 0; b < bars; b++) {
          const s = Math.floor(b * bin);
          const e = Math.max(s + 1, Math.floor((b + 1) * bin));
          let p = 0;
          for (let i = s; i < e && i < data.length; i++) {
            const v = Math.abs(data[i] ?? 0);
            if (v > p) p = v;
          }
          barPeaks[b] = p;
          if (p > maxPeak) maxPeak = p;
        }
        const rr = (
          ctx as unknown as {
            roundRect?: (x: number, y: number, w: number, h: number, r: number) => void;
          }
        ).roundRect;
        ctx.beginPath();
        for (let b = 0; b < bars; b++) {
          const amp = shapeWaveAmp(barPeaks[b] ?? 0, maxPeak);
          const h = Math.max(minH, amp * height * 0.92);
          const x = b * step;
          const y = Math.round(cy - h / 2);
          const hh = Math.round(h);
          if (typeof rr === 'function') rr.call(ctx, x, y, barW, hh, radius);
          else ctx.rect(x, y, barW, hh);
        }
        ctx.fill();
        ctx.closePath();
      },
    });
    wsRef.current = ws;
    ws.on('ready', () => {
      setReady(true);
      const d = ws.getDuration();
      if (Number.isFinite(d) && d > 0) setDuration(d);
    });
    ws.on('audioprocess', () => setPosition(ws.getCurrentTime()));
    ws.on('seeking', () => setPosition(ws.getCurrentTime()));
    ws.on('finish', () => {
      setPlaying(false);
      setPosition(ws.getDuration());
    });
    void ws.load(src);
    return () => {
      ws.destroy();
      wsRef.current = null;
    };
  }, [src]);

  const toggle = async (): Promise<void> => {
    const ws = wsRef.current;
    if (!ws || !ready) return;
    if (ws.isPlaying()) {
      ws.pause();
      setPlaying(false);
    } else {
      await ws.play();
      setPlaying(true);
    }
  };

  const elapsed = playing || position > 0 ? position : 0;

  return (
    <div className="flex min-h-[44px] flex-1 items-center gap-3 rounded-pill bg-n-100 px-3 py-2 min-w-0">
      <button
        type="button"
        onClick={onDiscard}
        aria-label="Descartar gravação"
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-pill text-danger-500 hover:bg-danger-50"
      >
        <Trash2 size={16} />
      </button>
      <button
        type="button"
        onClick={() => void toggle()}
        aria-label={playing ? 'Pausar' : 'Reproduzir'}
        disabled={!ready}
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center text-n-900 disabled:opacity-40"
      >
        {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
      </button>
      <div ref={containerRef} className="flex-1 min-w-0" />
      <span className="font-mono text-[12px] text-n-700 tabular-nums whitespace-nowrap">
        {formatTime(elapsed)} / {formatTime(duration)}
      </span>
      <span className="flex flex-shrink-0 items-center gap-1 rounded-pill border border-n-300 bg-n-0 px-2 py-0.5 text-[11px] text-n-700">
        <AudioLines size={12} />
        Original
      </span>
    </div>
  );
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${pad(m)}:${pad(s)}`;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
